"use client";
import { usePathname, useRouter } from "@/lib/router-compat";
import { useCallback, useEffect, useRef, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { CenteredLoading } from "@/components/common/CenteredLoading";
import {
  localAuthRepository,
  staffSessionRevisionKey,
} from "@/repository/localAuthRepository";
import { authRepository } from "@/repository/authRepository";
import { recoverStaffSession } from "@/infrastructure/httpDao";
import { isCancel } from "axios";
import { WelcomeScreen } from "@/components/auth/WelcomeScreen";
import { useToast } from "@/hooks/use-toast";
import { UserInfo } from "@/types/common";
import { ChatbotFAB } from "@/components/ai-chatbot/ChatbotFAB";
import { isPublicPage } from "@/lib/publicPaths";
import { isPlatformOwner } from "@/lib/platformRoles";
import { StaffModeProvider, useStaffMode } from "@/contexts/StaffModeContext";
import { getPlatformRole, hasPlatformAccess } from "@/lib/staffMode";
import { Button } from "@/components/ui/button";
import { RefreshCw, WifiOff } from "lucide-react";

type AuthSurface = "public" | "protected";

// 認証状態を管理するコンポーネント（レイアウトは含まない）
function AuthChecker({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { toast } = useToast();
  const { mode } = useStaffMode();
  const authSurface: AuthSurface = isPublicPage(pathname)
    ? "public"
    : "protected";

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);
  const [authCheckFailed, setAuthCheckFailed] = useState(false);
  const [staffSessionChanged, setStaffSessionChanged] = useState(false);
  const [checkedAuthSurface, setCheckedAuthSurface] =
    useState<AuthSurface | null>(null);
  const authCheckRequestIdRef = useRef(0);
  const rejectedStaffRevisionRef = useRef<string | null>(null);

  // ユーザー情報を保持するためのState（ページ遷移しても消えないようにLayoutレベルで持つ）
  const [cachedUserInfo, setCachedUserInfo] = useState<UserInfo | null>(null);

  // プロフィール未完了ユーザーがアクセス可能なページ
  const allowedPagesForIncompleteProfile = ["/account/register"];

  const allowedPagesForUnverified = ["/account/verify-email", "/verify-code"];
  const allowedPagesForPlatformUser = ["/operator", "/settings"];
  const ownerOnlyPlatformPages = [
    "/operator/platform-accesses",
    "/operator/data-reconciliation",
    "/operator/data-migrations",
    "/operator/app-status",
  ];
  // パスがプロフィール未完了ユーザーに許可されているかチェック
  const isAllowedPageForIncompleteProfile = (path: string) => {
    return allowedPagesForIncompleteProfile.some(
      (allowedPath) => path === allowedPath || path.startsWith(allowedPath)
    );
  };

  const isAllowedPageForUnverified = (path: string) => {
    return allowedPagesForUnverified.some(
      (allowedPath) => path === allowedPath || path.startsWith(allowedPath)
    );
  };

  const isAllowedPageForPlatformUser = (path: string) => {
    return allowedPagesForPlatformUser.some(
      (allowedPath) => path === allowedPath || path.startsWith(allowedPath)
    );
  };

  const isOwnerOnlyPlatformPage = (path: string) => {
    return ownerOnlyPlatformPages.some(
      (ownerPath) => path === ownerPath || path.startsWith(`${ownerPath}/`)
    );
  };

  // 1. ユーザー情報取得。
  // access token は sessionStorage のため、新しいタブやブラウザ再起動後には存在しない。
  // その場合も httpOnly Cookie の refresh token からセッションを復旧する。
  const fetchUserInfo = useCallback(
    async (surface: AuthSurface): Promise<void> => {
      const requestId = ++authCheckRequestIdRef.current;
      const startedRevision = localAuthRepository.getStaffSessionRevision();
      setStaffSessionChanged(false);

      // 顧客向け公開ページではスタッフ /me を呼ばない。
      // 会員JWTを持っている場合にスタッフ認証として扱われ、カード登録前に消えるのを防ぐ。
      if (surface === "public") {
        setCachedUserInfo(null);
        setIsLoggedIn(false);
        setIsCheckingProfile(false);
        setCheckedAuthSurface(surface);
        setAuthCheckFailed(false);
        return;
      }

      setIsCheckingProfile(true);
      setAuthCheckFailed(false);

      try {
        if (!localAuthRepository.isStaffSessionCurrent()) {
          // 判定中は旧アカウントの画面を隠し、通常のリクエストの境界も維持する。
          setCachedUserInfo(null);
          setIsLoggedIn(false);
          const recovered = await recoverStaffSession();
          if (requestId !== authCheckRequestIdRef.current) return;
          if (recovered) {
            rejectedStaffRevisionRef.current = null;
            setCachedUserInfo(recovered);
            setIsLoggedIn(true);
          } else {
            rejectedStaffRevisionRef.current = startedRevision;
            setStaffSessionChanged(true);
          }
          return;
        }
        const token = localAuthRepository.getToken();
        const storedUserInfo = localAuthRepository.getUserInfo();

        // 同じタブ内でトークンとユーザー情報が揃っていればキャッシュを利用する。
        if (
          token &&
          storedUserInfo &&
          Object.prototype.hasOwnProperty.call(storedUserInfo, "platformRole")
        ) {
          setCachedUserInfo(storedUserInfo);
          setIsLoggedIn(true);
          setAuthCheckFailed(false);
          return;
        }

        // access token が無い場合、httpDao の 401 interceptor が refresh Cookie で
        // access token を再発行して /me を再試行する。
        const response = await authRepository.fetchIamWithRetry();
        if (requestId !== authCheckRequestIdRef.current) return;

        const userInfo = response.data;
        if (userInfo) {
          setCachedUserInfo(userInfo as UserInfo);
          setIsLoggedIn(true);
          setAuthCheckFailed(false);
        } else {
          setCachedUserInfo(null);
          setIsLoggedIn(false);
        }
      } catch (error) {
        if (requestId !== authCheckRequestIdRef.current) return;
        if (
          isCancel(error) &&
          startedRevision !== localAuthRepository.getStaffSessionRevision()
        ) {
          void fetchUserInfo(surface);
          return;
        }

        console.error("ユーザー情報取得エラー:", error);
        const statusCode = (
          error as { response?: { status?: number } } | undefined
        )?.response?.status;
        if (statusCode === 401) {
          setIsLoggedIn(false);
          setCachedUserInfo(null);
          setAuthCheckFailed(false);
        } else {
          // デプロイ中の5xxや通信断を未ログインと誤判定しない。
          // 保存済みセッションを維持し、同じ画面で再試行できるようにする。
          setAuthCheckFailed(true);
        }
      } finally {
        if (requestId === authCheckRequestIdRef.current) {
          setIsCheckingProfile(false);
          setCheckedAuthSurface(surface);
        }
      }
    },
    []
  );

  // 2. アクセス制御（pathname変更時にキャッシュを使って即時判定）
  const checkAccess = () => {
    // ログインページなどの特殊なパスはスキップ
    if (
      pathname.startsWith("/verify-code") ||
      pathname.startsWith("/join") ||
      pathname.startsWith("/invite/accept") ||
      pathname.startsWith("/staff/invite/accept") ||
      pathname.includes("/new-password")
    ) {
      return;
    }

    // 未ログイン時
    if (!isLoggedIn) {
      if (authSurface === "protected" && pathname !== "/") {
        router.replace("/");
      }
      return;
    }

    // ログイン済み時（cachedUserInfoがある前提）
    if (cachedUserInfo) {
      const canUsePlatform = hasPlatformAccess(cachedUserInfo);
      const isPlatformMode = mode === "platform" && canUsePlatform;
      const profileCompleted = cachedUserInfo.is_profile_completed;
      const verified = cachedUserInfo.is_verified ?? true;

      if (!verified) {
        if (!isAllowedPageForUnverified(pathname)) {
          router.replace("/account/verify-email");
        }
      } else if (isPlatformMode) {
        if (
          pathname === "/" ||
          pathname === "/dashboard" ||
          isAllowedPageForIncompleteProfile(pathname) ||
          isAllowedPageForUnverified(pathname) ||
          !isAllowedPageForPlatformUser(pathname) ||
          (isOwnerOnlyPlatformPage(pathname) &&
            !isPlatformOwner(getPlatformRole(cachedUserInfo)))
        ) {
          router.replace("/operator/tenant-payments");
        }
      } else {
        if (pathname.startsWith("/operator")) {
          router.replace("/dashboard");
          return;
        }
        // プロフィール未完了かつ許可されていないページ
        if (!profileCompleted && !isAllowedPageForIncompleteProfile(pathname)) {
          router.replace("/account/register");
        }

        // プロフィール完了済みかつ登録系ページ（逆に戻れないようにする）
        if (profileCompleted && isAllowedPageForIncompleteProfile(pathname)) {
          router.replace("/dashboard");
        }

        // プロフィール完了済みかつルートパスの場合はダッシュボードにリダイレクト
        if (profileCompleted && pathname === "/") {
          router.replace("/dashboard");
        }
      }

      if (
        verified &&
        profileCompleted &&
        isAllowedPageForUnverified(pathname)
      ) {
        router.replace("/dashboard");
      }
    }
  };

  // ログイン成功トーストは初回マウント時だけ表示する。
  useEffect(() => {
    if (sessionStorage.getItem("showLoginSuccessToast") === "true") {
      toast({
        title: "ログインしました",
        description: "ダッシュボードへようこそ",
        variant: "success",
      });
      sessionStorage.removeItem("showLoginSuccessToast");
    }
  }, [toast]);

  // 初回表示と公開・管理画面の境界をまたぐ時だけセッションを確認する。
  // 管理画面内のURL変更では認証済みシェルを維持し、本文だけを切り替える。
  useEffect(() => {
    void fetchUserInfo(authSurface);
  }, [authSurface, fetchUserInfo]);

  // Observe only the staff login generation, not arbitrary localStorage writes.
  // Focus/pageshow also cover suspended tabs and back-forward cache restoration.
  useEffect(() => {
    const checkSession = () => {
      if (
        authSurface === "protected" &&
        !localAuthRepository.isStaffSessionCurrent() &&
        rejectedStaffRevisionRef.current !==
          localAuthRepository.getStaffSessionRevision()
      )
        void fetchUserInfo(authSurface);
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea && event.storageArea !== window.localStorage)
        return;
      if (event.key === null || event.key === staffSessionRevisionKey) {
        checkSession();
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") checkSession();
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", checkSession);
    window.addEventListener("pageshow", checkSession);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", checkSession);
      window.removeEventListener("pageshow", checkSession);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [authSurface, fetchUserInfo]);

  // 同じタブ内のログイン・ログアウト処理から認証状態変更を反映する。
  // access token とユーザー情報はタブ単位で保持し、新しいタブは初回の
  // fetchUserInfo で refresh Cookie から復旧する。localStorage の変更全般を
  // 監視すると、認証期限が異なる複数タブ間で /me の再取得が循環する。
  useEffect(() => {
    const handleAuthStateChanged = (event: Event) => {
      const explicitlyLoggedOut =
        event instanceof CustomEvent && event.detail?.isLoggedIn === false;

      if (explicitlyLoggedOut) {
        setCachedUserInfo(null);
        setIsLoggedIn(false);
        setIsCheckingProfile(false);
        setAuthCheckFailed(false);
        return;
      }

      const currentSurface: AuthSurface = isPublicPage(window.location.pathname)
        ? "public"
        : "protected";
      void fetchUserInfo(currentSurface);
    };
    window.addEventListener("authStateChanged", handleAuthStateChanged);

    return () => {
      window.removeEventListener("authStateChanged", handleAuthStateChanged);
    };
  }, [fetchUserInfo]);

  // パス・認証状態・ユーザー情報が変わったときにアクセス権をチェックする。
  // 未認証時も実行し、保護URLからログイン画面へ戻す。
  useEffect(() => {
    if (
      !isCheckingProfile &&
      !authCheckFailed &&
      checkedAuthSurface === authSurface
    ) {
      checkAccess();
    }
  }, [
    pathname,
    authSurface,
    checkedAuthSurface,
    isCheckingProfile,
    authCheckFailed,
    isLoggedIn,
    cachedUserInfo,
    mode,
  ]);

  // 公開パスは401リダイレクト抑止と同じ境界付き判定を使う。
  // `/survey/:token` は公開だが、管理画面の `/surveys` は一致しない。
  const isBasicWithoutLayout = isPublicPage(pathname);

  // /パスの場合は認証状態を確認
  const isRootPath = pathname === "/";

  // 最終的なレイアウト判定
  const isWithoutLayout = isBasicWithoutLayout || (isRootPath && !isLoggedIn);

  if (isCheckingProfile || checkedAuthSurface !== authSurface) {
    return <CenteredLoading />;
  }

  if (authSurface === "protected" && staffSessionChanged) {
    return <WelcomeScreen />;
  }

  if (authSurface === "protected" && authCheckFailed) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-slate-50 p-4 sm:p-6">
        <section
          role="alert"
          className="w-full max-w-md rounded-(--radius-container) border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-8"
        >
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-slate-100 text-slate-600">
            <WifiOff aria-hidden="true" className="size-6" />
          </div>
          <h1 className="mt-5 text-lg font-bold text-slate-950">
            ログイン状態を確認できませんでした
          </h1>
          <p className=" text-sm leading-6 text-slate-600">
            セッションは保持されています。通信状態を確認して、もう一度お試しください。
          </p>
          <Button
            type="button"
            className="mt-6 w-full"
            onClick={() => void fetchUserInfo(authSurface)}
          >
            <RefreshCw aria-hidden="true" className="size-4" />
            再試行
          </Button>
        </section>
      </main>
    );
  }

  // 認証失敗後、リダイレクトが完了するまで保護ルートのReactツリーを
  // マウントしない。APIを認可の境界としつつ、不要なAPI呼び出しも防ぐ。
  if (authSurface === "protected" && pathname !== "/" && !isLoggedIn) {
    return <CenteredLoading />;
  }

  if (isWithoutLayout) {
    if (isRootPath && !isLoggedIn) {
      return <WelcomeScreen />;
    }

    // Header は認証済みレイアウトの Provider（Sidebar / Facility / PageChrome /
    // OrganizationBranding）に依存するため、ここでは描画しない。
    // 描画すると useSidebar が例外を投げ、/join などの公開ページが白画面になる。
    return <>{children}</>;
  }

  // ルートパスでログイン済みの場合のアクセス制御
  if (isRootPath && isLoggedIn && cachedUserInfo) {
    const isVerified = cachedUserInfo.is_verified ?? true;
    const isProfileCompleted = cachedUserInfo.is_profile_completed;

    // メール未認証、またはプロフィール未完了の場合はコンテンツを表示しない（リダイレクト待ち）
    if (!isVerified || !isProfileCompleted) {
      return <CenteredLoading />;
    }
  }

  const isChatPage = pathname?.startsWith("/chat");

  return (
    <>
      {children}
      <ChatbotFAB compact={isChatPage} />
    </>
  );
}

// メインのラッパー（認証チェックのみ）
export function LayoutWrapper({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Toaster />
      <StaffModeProvider>
        <AuthChecker>{children}</AuthChecker>
      </StaffModeProvider>
    </>
  );
}
